import { type JanusCtx, type Row, ec, ecLean, handBack, insertRoll, itemField, record, requiredInt, scalar } from '../context.js';
import { type JanusInput } from '../janus.js';
import { postEffectConditions } from '../post-effect-conditions.js';
import { rollDiceFaces } from './roll-dice-arr.js';
import { isPaoeEntry } from '../paoe-entry-mode.js';
import { spellLevelOf, upcastRoll } from '../upcast.js';
import { effectTargets } from '../is-target-valid-for-effect.js';
import { variableDice } from '../variable-amount.js';
import { rollTypeRollStacks, rollTypeVariable } from './roll-type-roll-shared.js';
import { checkScalableCantrip } from '../check-scalable-cantrip.js';
import { is_numeric } from '../../php-compat/values.js';

const TEMP_V_ROLL = '5E2689A2-0F7D-1E27-F4D5-CA09CA5C2591';

/** Temporary vitality replaces a smaller pool; it never stacks. Shared by grants and status upkeep. */
export function grantTemporaryVitality(target: Row, result: number): boolean {
  const vitality = record(record(target.computed)?.vitality);
  const current = requiredInt(vitality?.v_temp ?? ecLean(target, 'v_temp'), 'temp_v_roll:current-not-held');
  if (result <= current) return false;
  const next = Math.max(0, result);
  ec(target).v_temp = next;
  if (vitality) { vitality.v_temp = next; vitality.base_v_temp = next; }
  return true;
}

/** includes/functions/janus/rolls/temp_v_roll.php:temp_v_roll */
export function tempVRoll(ctx: JanusCtx, input: JanusInput, ability: Row, caster: Row, targets: Row[]): void {
  const eligible = effectTargets(ctx, input, ability, caster, targets);
  if (!eligible) return;
  if (targets.length !== 1) handBack('temp_v_roll:multi-target-validity-not-modeled');
  // variable_2 makes janus leave castLevel empty even for a numeric level picker; preserve the unresolved growth case.
  if (!isPaoeEntry(input) && ctx.castLevel == null && input.useAbilityVariable2 != null
      && is_numeric(input.useAbilityVariable1) && ['spell', 'curse', 'menta'].includes(String(itemField(ability, 'item_type')))
      && spellLevelOf(ctx, ability) > 0 && Number(input.useAbilityVariable1) > spellLevelOf(ctx, ability)
      && (ctx.read.has(ability, 'dice_upcast') || ctx.read.has(ability, 'mod_upcast')))
    handBack('temp_v_roll:upcast-not-modeled');
  const authoredDice = ctx.read.value(ability, 'dice');
  const baseDice = rollTypeVariable(ctx, input, caster, variableDice(authoredDice, input.useAbilityVariable1), 'temp_v_roll');
  const die = rollTypeVariable(ctx, input, caster, ctx.read.value(ability, 'die'), 'temp_v_roll');
  const rawMod = ctx.read.value(ability, 'mod');
  const baseMod = rollTypeVariable(ctx, input, caster, rawMod, 'temp_v_roll');
  const up = upcastRoll(ctx, ability, baseDice, baseMod);
  // temp_v_roll.php's double-dip guard: X already supplies the dice; only the modifier may grow again.
  const dice = checkScalableCantrip(ctx, ability, caster, scalar(authoredDice) === '[x]' ? baseDice : up.dice);
  const mod = up.mod;
  const target = [...eligible][0]!;
  const advDisStacks = rollTypeRollStacks(ctx, ability, caster, target, TEMP_V_ROLL, 'temp_v_roll');
  const rolled = rollDiceFaces(ctx, dice, die, advDisStacks);
  const result = rolled.total + mod;
  const fullFaces: unknown[] = [...rolled.faces];
  if (baseMod) fullFaces.push([/^-?\d+$/.test(String(rawMod)) ? String(rawMod) : String(baseMod), 'Temp V Mod']);
  if (mod !== baseMod) fullFaces.push([mod - baseMod, 'Upcast Mod']);
  // post_effect_conditions applies the spell status before temp vitality.
  postEffectConditions(ctx, input, ability, caster, target);
  const granted = grantTemporaryVitality(target, result);
  insertRoll(ctx, { roll_type_1: TEMP_V_ROLL,
    roll_type_2: granted ? '' : 'current_v_temp_not_exceeded',
    roll_item_id: input.abilityId, by: caster, to: target, roll_result: result,
    rolls_full: fullFaces, dice, die, mod, adv_dis_stacks: advDisStacks, dc: 0 });
}
